/**
 * DataTable — la tabla del sistema.
 *
 * 🔴 **Es el componente con más deuda cubierta de todo el port.** El repo no
 * tiene ninguno: cada módulo escribe su propio `<table>`, y con eso cada uno
 * reinventa la densidad, el orden, la fila alterna y el estado de selección.
 * `@tanstack/react-table` está instalado **y sin usar** desde hace meses.
 *
 * Lo que fija, y que hoy se decide en cada pantalla por separado:
 *
 * ⚠️ **La densidad va en `data-density`, no en una clase.** La misma tabla pasa
 * de `dense` a `cozy` sin recomponer su className, y el bloque
 * `pointer: coarse` del CSS sube el padding en un teléfono. El default es
 * `dense` porque un ERP muestra muchas filas.
 *
 * ⚠️ **El orden se declara con `aria-sort` en el `<th>`**, que es el atributo
 * que el lector de pantalla anuncia. Sin él, la flechita es información que
 * solo existe para quien la ve.
 *
 * ⚠️ **`numeric` en una columna aplica `.cl-num`**: monoespaciado, tabular y
 * alineado a la derecha. Es lo que permite comparar montos de un vistazo, y es
 * exactamente lo que se pierde cuando cada tabla se escribe a mano.
 *
 * ⚠️ `minWidth` con el envoltorio `.cl-tablewrap`, que desplaza en horizontal:
 * una tabla de ocho columnas no entra en un teléfono, y comprimirla hasta que
 * entre deja las celdas ilegibles. Se desplaza, no se aplasta.
 */
import { Fragment, useMemo, useState } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'

export interface DataTableColumn<R> {
  key: string
  header: ReactNode
  /** Monoespaciado, tabular y a la derecha. Para montos y cantidades. */
  numeric?: boolean
  /**
   * Deja ordenar por esta columna con un clic en su encabezado.
   *
   * 🔴 **Si la tabla la pagina el SERVIDOR, hay que pasar además `sort` y
   * `onSortChange`** y mandar el orden al backend (`?ordering=`). Sin eso el
   * componente ordena las filas que recibió, o sea **solo la página visible**,
   * y quien mira cree que ordenó las 400. Es el peor modo de fallar: no hay
   * error, hay un orden que miente. Medido en el producto: 33 de las 117
   * tablas paginan por servidor.
   */
  sortable?: boolean
  /**
   * Con qué valor se ordena, cuando no es el que se muestra.
   *
   * ⚠️ Hace falta más seguido de lo que parece: una fecha que `render`
   * imprime `15-09-2026` se ordenaría como texto —y `02-01` iría antes que
   * `15-09` de cualquier año— y un monto formateado con separadores de
   * miles ordena por su primer dígito. Sin esto, el orden miente.
   */
  sortValue?: (row: R) => string | number | Date | null | undefined
  width?: number | string
  /** ⚠️ Recibe el índice además de la fila: numerar un ranking («#») es
   *  un caso común y sin él hay que salirse del componente. */
  render?: (row: R, index: number) => ReactNode
}

export interface SortState {
  key: string
  dir: 'asc' | 'desc'
}

export interface DataTableProps<R> extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  columns?: DataTableColumn<R>[]
  rows?: R[]
  rowKey?: (row: R, index: number) => string | number
  density?: 'dense' | 'cozy'
  onRowClick?: (row: R) => void
  /**
   * Cuáles filas responden al clic, cuando no son todas.
   *
   * 🔴 Sin esto, `onRowClick` marca la tabla ENTERA como clicable: el CSS pone
   * cursor de mano y fondo al pasar por encima en filas que no hacen nada, o sea
   * la tabla promete una acción que no ocurre. Y al revés de un botón
   * deshabilitado, no hay nada que explique por qué esa fila no responde.
   *
   * Medido en el producto antes de agregarla: **6 tablas en 3 archivos** tienen
   * el clic acotado a algunas filas —la conciliación bancaria (una línea ya
   * conciliada no se selecciona), el balance y el EERR (solo bajan al detalle
   * las cuentas que existen en el plan) y el detalle del documento (solo las
   * líneas con producto)—. No se agregó por el primer caso: se midió que fuera
   * un patrón.
   *
   * ⚠️ Omitirla significa «todas», que es el comportamiento anterior.
   */
  rowClickable?: (row: R, index: number) => boolean
  selectedKey?: string | number
  /**
   * Clase extra por fila, para atenuar o destacar segun el estado del registro.
   *
   * ⚠️ Es lo unico que no se puede resolver con `render`: una serie inactiva
   * o un pago anulado se atenuan ENTEROS, y hacerlo celda por celda deja los
   * bordes y el fondo de la fila sin atenuar. Medido en el producto: 9 tablas
   * lo hacian con un `className` condicional en su `<tr>`.
   *
   * 🔴 No sustituye a `aria-selected` ni a un estado accesible: el color solo
   * refuerza, nunca informa por si mismo.
   */
  rowClassName?: (row: R, index: number) => string | undefined
  /**
   * El detalle que se dibuja BAJO la fila, a lo ancho de la tabla.
   *
   * 🔴 Es el bloqueo que más tablas tenía después del pie de totales: un
   * elemento que emite DOS `<tr>` —el propio y, si está abierto, uno con su
   * detalle— contra un componente que emitía uno por fila. Medido antes de
   * construirlo: 5 tablas en el producto (movimientos y conteos de inventario,
   * períodos de nómina, y las líneas del documento de venta y del pedido).
   *
   * ⚠️ El `colSpan` sale de `columns.length` y no de un número escrito a mano,
   * que es el mismo argumento del pie: a mano se desalinea apenas alguien
   * agrega o quita una columna, y ahí el detalle deja un hueco o desborda la
   * tabla. Es el error que ya traían dos de las cinco (`colSpan={10}` y
   * `colSpan={8}` literales).
   *
   * ⚠️ **El `<td>` del detalle va SIN padding**, a propósito: las tablas que lo
   * usan no coinciden en cuánto quieren —una pinta una grilla con `px-6 py-4` y
   * otra inserta un componente que trae su propio layout y necesita `0`—, así
   * que el espaciado lo decide el contenido. Heredar el padding de `.cl-table
   * td` dejaría a la segunda sin forma de quitarlo.
   *
   * 🔴 **El detalle NO responde a `onRowClick`.** En las tablas que esto
   * reemplaza el clic de la fila es justamente lo que expande y colapsa, así
   * que propagarlo al detalle haría que cualquier clic dentro —un botón, un
   * texto que se quiere seleccionar— cerrara lo que se acaba de abrir.
   *
   * ⚠️ Tampoco hereda `rowClassName`: en el producto una fila anulada se atenúa
   * y su detalle no, que es donde se va a leer por qué se anuló.
   *
   * El estado sigue viviendo en la pantalla, como la selección: el componente
   * pregunta con `rowExpanded` y dibuja con `expandedRow`.
   */
  expandedRow?: (row: R, index: number) => ReactNode
  /** Cuáles filas están expandidas. Sin esto, ninguna. */
  rowExpanded?: (row: R, index: number) => boolean
  sort?: SortState
  onSortChange?: (sort: SortState) => void
  minWidth?: number | string
  /**
   * Alto máximo de la tabla, con el ENCABEZADO FIJO mientras se scrollea.
   *
   * 🔴 El encabezado fijo tiene que resolverse ACÁ y no con un `sticky` desde
   * afuera: `.cl-tablewrap` trae `overflow-x: auto`, y un ancestro con overflow
   * se vuelve el contenedor de scroll de cualquier `sticky` descendiente — así
   * que un encabezado «pegado» al scroll de un contenedor externo se va con las
   * filas sin ningún error. Medido en el navegador el 2026-09-29. Con esta prop
   * es el mismo envoltorio el que scrollea, y el `sticky` sí tiene dónde pegarse.
   *
   * ⚠️ `overflow: hidden` hace lo mismo que `auto` en ese sentido: un `sticky`
   * dentro de un contenedor `overflow-hidden` tampoco se pega.
   */
  maxHeight?: number | string
  /** Descripción para el lector de pantalla. Va en un `<caption>` invisible. */
  /**
   * Qué mostrar mientras llegan los datos, en lugar de la tabla.
   *
   * ⚠️ Va EN LUGAR y no adentro: el patrón que esto reemplaza metía el aviso en
   * un `<tr><td colSpan={7}>Cargando…</td></tr>`, lo que deja los encabezados de
   * una tabla que todavía no tiene nada.
   */
  loading?: boolean
  loadingNode?: ReactNode
  /**
   * Qué mostrar cuando no hay filas.
   *
   * 🔴 Acá va un `<EmptyState>`, que trae `role="status"` + `aria-live`. El
   * patrón que reemplaza —el mensaje dentro de un `<td colSpan>`— **no anuncia
   * nada**: al aplicar un filtro que vacía la tabla, quien usa lector de
   * pantalla no se entera de que la lista quedó vacía. Medido: 28 tablas del
   * producto lo hacían así.
   */
  empty?: ReactNode
  /**
   * Pie de totales: un valor por clave de columna.
   *
   * 🔴 Es un MAPA por columna y no un `<tr>` crudo, y eso es lo que resuelve.
   * El pie escrito a mano se desalinea del cuerpo apenas alguien agrega o
   * reordena una columna, y pierde el `cl-num` que alinea los montos a la
   * derecha — o sea el total queda en otra posición que la columna que suma.
   * Acá la alineación y el ancho los hereda de `columns`, así que no pueden
   * divergir.
   *
   * Las columnas iniciales SIN valor se fusionan bajo `footerLabel` con un
   * `colSpan`, que es exactamente el patrón de las 12 tablas que esto
   * reemplaza (`<td colSpan={3}>Totales</td>` y después una celda por monto).
   *
   * ⚠️ No se dibuja cuando no hay filas: un «Totales 0» sobre una tabla vacía
   * es ruido, y el caso de la tabla vacía ya lo resuelve `empty`.
   */
  footer?: Partial<Record<string, ReactNode>>
  /** La etiqueta del pie. Ocupa las columnas previas a la primera con total. */
  footerLabel?: ReactNode
  /**
   * El panel que envuelve la tabla — fondo, borde, esquinas y sombra.
   *
   * 🔴 Va EN EL COMPONENTE y no en cada pantalla, y esto se aprendió migrando:
   * con el panel a cargo del consumidor aparecieron CUATRO envoltorios distintos
   * para la misma tabla —con `overflow-hidden` y sin él, con `rounded-card` y con
   * `rounded-lg`, con sombra y sin ella, y tres pantallas sin panel— o sea el
   * mismo problema que el componente vino a resolver, una capa más afuera.
   *
   * ⚠️ El recorte no es cosmético: sin él la tabla desborda las esquinas
   * redondeadas y asoma una línea por debajo de la última fila.
   *
   * `false` para una tabla que ya vive dentro de otro panel (un modal, una
   * tarjeta), donde un segundo borde se ve como una caja dentro de otra.
   */
  surface?: boolean
  caption?: string
  className?: string
}

/**
 * Ordena por el valor crudo, no por lo que se ve.
 *
 * Los vacíos van SIEMPRE al final, suban o bajen: un `—` no es «el menor», es
 * la ausencia de dato, y mezclarlo entre los valores hace que una columna
 * ordenada descendente arranque con las filas que no tienen nada.
 *
 * El texto se compara con `localeCompare` y `numeric`, así «Item 2» queda antes
 * que «Item 10» y los acentos no mandan «Ávila» al final del alfabeto.
 */
const vacio = (v: unknown) => v === null || v === undefined || v === ''

function comparar(a: unknown, b: unknown): number {
  if (a instanceof Date || b instanceof Date) return Number(a) - Number(b)
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * ⚠️ El constraint es `object` y NO `Record<string, unknown>`, que es lo que
 * traía el sistema. Una **interface** de TypeScript no es asignable a
 * `Record<string, unknown>` —le falta la index signature— y en este repo los
 * tipos de dominio son todos interfaces, así que con el constraint original
 * el componente no se podía usar con NINGÚN tipo del producto:
 * `Type 'Contract' does not satisfy the constraint`.
 *
 * El costo de aflojarlo es que el acceso por clave necesita un cast, y por eso
 * queda **uno solo, acá adentro**, en vez de 61 en los consumidores. Lo que el
 * consumidor ve sigue tipado: `columns`, `rowKey` y `render` reciben `R`.
 */
/**
 * La superficie del producto. `overflow-hidden` lo pone `.cl-tablewrap`, que ya
 * trae `overflow-x: auto` — y eso alcanza para recortar las esquinas.
 */
const PANEL = 'bg-glass backdrop-blur-glass rounded-card border border-divider shadow-card'

export function DataTable<R extends object>({
  columns = [],
  rows = [],
  rowKey = (r, i) => (r as { id?: string | number }).id ?? i,
  density = 'dense',
  onRowClick,
  rowClickable,
  selectedKey,
  rowClassName,
  expandedRow,
  rowExpanded,
  sort,
  onSortChange,
  minWidth = 640,
  maxHeight,
  loading = false,
  loadingNode,
  empty,
  footer,
  footerLabel = 'Totales',
  surface = true,
  caption,
  className = '',
  style,
  ...rest
}: DataTableProps<R>) {
  // Orden PROPIO cuando nadie lo controla desde afuera: si el componente
  // exige que cada pantalla implemente su estado y su comparador, en la
  // práctica ninguna lo hace y las 122 tablas del producto siguen sin poder
  // ordenarse, que es la situación de hoy.
  const [ordenPropio, setOrdenPropio] = useState<SortState | undefined>()
  const controlado = onSortChange !== undefined
  const orden = controlado ? sort : (sort ?? ordenPropio)

  const filas = useMemo(() => {
    // Con orden controlado las filas llegan ya ordenadas (normalmente por el
    // backend): reordenarlas acá pelearía contra eso.
    if (controlado || !orden) return rows
    const col = columns.find(c => c.key === orden.key)
    if (!col) return rows
    const valor = (r: R) =>
      col.sortValue ? col.sortValue(r) : (r as Record<string, unknown>)[col.key]
    const signo = orden.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = valor(a)
      const vb = valor(b)
      // 🔴 Los vacíos quedan FUERA del signo, o al invertir el orden
      // encabezarían la tabla: una columna descendente arrancaría con las
      // filas que no tienen dato. Lo encontró su propio test.
      if (vacio(va) || vacio(vb)) return vacio(va) && vacio(vb) ? 0 : vacio(va) ? 1 : -1
      return signo * comparar(va, vb)
    })
  }, [rows, columns, orden, controlado])

  const cambiarOrden = (s: SortState) =>
    onSortChange ? onSortChange(s) : setOrdenPropio(s)

  // Los dos estados salen ANTES de la tabla: mostrar encabezados de columnas
  // sobre un cuerpo vacío no informa, y el aviso tiene que poder anunciarse.
  if (loading) {
    return (
      <div className={`cl-tablewrap ${surface ? PANEL : ''}`.trim()} aria-busy="true">
        {loadingNode ?? (
          <div className="px-4 py-8 text-center text-sm text-content-subtle">Cargando…</div>
        )}
      </div>
    )
  }
  if (!filas.length && empty) {
    return surface ? <div className={PANEL}>{empty}</div> : <>{empty}</>
  }

  return (
    <div
      className={`cl-tablewrap ${surface ? PANEL : ''} ${className}`.trim()}
      style={maxHeight !== undefined ? { maxHeight, overflowY: 'auto', ...style } : style}
      {...rest}
    >
      <table
        className="cl-table"
        data-density={density}
        data-sticky-header={maxHeight !== undefined ? 'true' : undefined}
        style={{ minWidth }}
      >
        {caption && <caption className="cl-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map(c => {
              const activa = orden?.key === c.key
              const dir = activa ? orden?.dir : undefined
              return (
                <th
                  key={c.key}
                  className={c.numeric ? 'cl-num' : undefined}
                  style={{ width: c.width }}
                  aria-sort={
                    activa ? (dir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      className="cl-sortbtn"
                      onClick={() =>
                        cambiarOrden({
                          key: c.key,
                          dir: activa && dir === 'asc' ? 'desc' : 'asc',
                        })
                      }
                    >
                      {c.header}
                      {/* La flecha es decorativa: lo que se anuncia es el
                          `aria-sort` del `<th>`. */}
                      <span aria-hidden="true" style={{ opacity: activa ? 1 : 0.35 }}>
                        {dir === 'asc' ? '↑' : '↓'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {filas.map((r, i) => {
            const key = rowKey(r, i)
            // El cursor y el `onClick` van juntos SIEMPRE: separarlos es lo que
            // produce una fila que se ve accionable y no responde.
            const clicable = !!onRowClick && (rowClickable?.(r, i) ?? true)
            const expandida = !!expandedRow && (rowExpanded?.(r, i) ?? false)
            const alterna = i % 2 === 1 || undefined
            return (
              <Fragment key={key}>
                <tr
                  className={rowClassName?.(r, i)}
                  data-alt={alterna}
                  data-clickable={clicable ? 'true' : undefined}
                  // Solo se declara en las tablas que expanden: en el resto,
                  // `aria-expanded` en una fila anuncia un plegado que no existe.
                  aria-expanded={expandedRow ? expandida : undefined}
                  aria-selected={
                    selectedKey !== undefined && selectedKey === key ? true : undefined
                  }
                  onClick={clicable ? () => onRowClick!(r) : undefined}
                >
                  {columns.map(c => (
                    <td key={c.key} className={c.numeric ? 'cl-num' : undefined}>
                      {c.render ? c.render(r, i) : ((r as Record<string, unknown>)[c.key] as ReactNode)}
                    </td>
                  ))}
                </tr>
                {expandida && (
                  // Comparte el `data-alt` de su fila para que el par se lea como
                  // una unidad cuando el detalle no trae fondo propio. Sin
                  // `data-clickable` ni `onClick`: ver el comentario de la prop.
                  <tr data-alt={alterna} data-detalle="true">
                    <td colSpan={columns.length}>{expandedRow!(r, i)}</td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
        {/* El pie va DESPUÉS del `<tbody>`: en HTML5 ese es su lugar, y el
            navegador lo pinta abajo igual. `filas.length` lo omite en una tabla
            sin datos. */}
        {footer && filas.length > 0 && (() => {
          // La etiqueta ocupa todo lo que hay ANTES de la primera columna con
          // total. Si la primera columna ya trae total, no hay etiqueta.
          const primera = columns.findIndex(c => c.key in footer)
          const span = primera < 0 ? columns.length : primera
          return (
            <tfoot>
              <tr>
                {span > 0 && <td colSpan={span}>{footerLabel}</td>}
                {columns.slice(span).map(c => (
                  <td key={c.key} className={c.numeric ? 'cl-num' : undefined}>
                    {footer[c.key] ?? null}
                  </td>
                ))}
              </tr>
            </tfoot>
          )
        })()}
      </table>
    </div>
  )
}
